// import { useCreateTableOrderMutation } from "pages/orders/services"
import { createContext, useState, useContext, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { getOrderTypePermissions, isPermissionAllowed } from "utils/function"

// Create the context
const OrderContext = createContext()

// Order types
export const ORDER_TYPES = {
  TAKE_AWAY: "takeAway",
  DINE_IN: "dineIn",
  TABLE_BOOKING: "tableBooking",
  DELIVERY: "delivery",
  PHONE_ORDER: "phoneOrder"
}
// const [createTableOrderAPI] = useCreateTableOrderMutation();

// Provider component
export function OrderProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const posData = useSelector((state) => state?.posData);
  const permissions = useMemo(() => getOrderTypePermissions(posData), [posData]);

  const [orderType, setOrderType] = useState(null)
  const [orderItems, setOrderItems] = useState([])
  const [customerData, setCustomerData] = useState(null)
  const [lastAddedItem, setLastAddedItem] = useState(null)
  const [dialogPrefill, setDialogPrefill] = useState(null);

  // —— New dialog state —— 
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState(null)

  // —— new update‐quantity dialog state ——
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  const [updateDialogItem, setUpdateDialogItem] = useState(null)
  const [updateDialogFromAdd, setUpdateDialogFromAdd] = useState(false);

  // Track items with their add-ons for dialog consistency
  const [itemsWithAddOns, setItemsWithAddOns] = useState({})

  // Function to handle when the continue button is clicked in the dialog
  const handleContinueClick = async (type, data) => {
    if (location?.pathname == "/calendar") {
      navigate('/')
    }
    // if(location.pathname == "/tables") {
    //   const res = await createTableOrderAPI();
    //   navigate("/order-details")
    // }
    setOrderType(type)
    setCustomerData(data)
  }

  // new helpers to open/close dialog
  const openDialog = (type, prefill = null) => {
    if ((type === ORDER_TYPES.TAKE_AWAY || type === 'takeAway') && !isPermissionAllowed(permissions, 'permission_take_away')) {
      return;
    }
    if ((type === ORDER_TYPES.DINE_IN || type === 'dineIn') && !isPermissionAllowed(permissions, 'permission_pos_walk_in_order')) {
      return;
    }
    if ((type === ORDER_TYPES.TABLE_BOOKING || type === 'tableBooking') && !isPermissionAllowed(permissions, 'permission_table_booking')) {
      return;
    }
    if ((type === ORDER_TYPES.PHONE_ORDER || type === 'phoneOrder') && !isPermissionAllowed(permissions, 'permission_phone_order')) {
      return;
    }

    setDialogType(type);
    setDialogPrefill(prefill);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setDialogType(null);
    setDialogPrefill(null);
  };

  const reopenDialog = () => {
    if (orderType) {
      setDialogType(orderType)
      setDialogOpen(true)
    }
  }

  // Helpers to show & hide dialog from elsewhere if you need
  const openUpdateDialog = (variants, fromAdd = true) => {
    setUpdateDialogItem(variants)
    setUpdateDialogOpen(true)
    setUpdateDialogFromAdd(fromAdd);
  }
  const closeUpdateDialog = () => {
    setUpdateDialogOpen(false)
    setUpdateDialogItem(null)
  }

  // Helper function to calculate the total price for an item
  const calculateItemTotalPrice = (item) => {
    // Base price
    const basePrice = item.price || 0

    // Add add-ons price
    const addOnsTotal = item.addOns ? item.addOns.reduce((sum, addon) => sum + addon.price, 0) : 0

    // Calculate total for this item
    return (basePrice + addOnsTotal) * item.quantity
  }

  // Function to add an item to the order
  // const addItemToOrder = (item) => {
  //   setOrderItems((prevItems) => {
  //     // Create a unique key for this item based on its ID and add-ons
  //     const addOnsKey = item.addOns
  //       ? item.addOns
  //           .map((addon) => `${addon.name}-${addon.price}`)
  //           .sort()
  //           .join("|")
  //       : ""
  //     const itemKey = `${item.id}-${item.size || ""}-${addOnsKey}`

  //     // Check if item with same configuration already exists in the order
  //     const existingItemIndex = prevItems.findIndex((i) => {
  //       // Check if IDs match
  //       if (i.id !== item.id) return false

  //       // Check if sizes match (if applicable)
  //       if (i.size !== item.size) return false

  //       // Check if add-ons match
  //       const iAddOnsKey = i.addOns
  //         ? i.addOns
  //             .map((addon) => `${addon.name}-${addon.price}`)
  //             .sort()
  //             .join("|")
  //         : ""
  //       const newAddOnsKey = item.addOns
  //         ? item.addOns
  //             .map((addon) => `${addon.name}-${addon.price}`)
  //             .sort()
  //             .join("|")
  //         : ""

  //       return iAddOnsKey === newAddOnsKey
  //     })

  //     let updatedItems = [...prevItems]

  //     if (existingItemIndex >= 0) {
  //       // If item exists, increase quantity by exactly 1
  //       const updatedItem = {
  //         ...updatedItems[existingItemIndex],
  //         quantity: updatedItems[existingItemIndex].quantity + 1,
  //       }

  //       // Recalculate the total price
  //       updatedItem.totalPrice = calculateItemTotalPrice(updatedItem)

  //       updatedItems[existingItemIndex] = updatedItem
  //     } else {
  //       // If item doesn't exist, add it with quantity 1 or the specified quantity
  //       const newItem = {
  //         ...item,
  //         quantity: item.quantity || 1,
  //       }

  //       // Make sure totalPrice is set correctly
  //       if (!newItem.totalPrice) {
  //         newItem.totalPrice = calculateItemTotalPrice(newItem)
  //       }

  //       updatedItems = [...prevItems, newItem]
  //     }

  //     // Update the items with add-ons tracking
  //     const updatedItem =
  //       existingItemIndex >= 0 ? updatedItems[existingItemIndex] : updatedItems[updatedItems.length - 1]

  //     setItemsWithAddOns((prev) => ({
  //       ...prev,
  //       [itemKey]: {
  //         ...updatedItem,
  //         quantity: updatedItem.quantity,
  //         totalPrice: updatedItem.totalPrice,
  //       },
  //     }))
  //     // setLastAddedItem(updatedItems)
  //     console.log("Updated items:", updatedItems)
  //     return updatedItems
  //   })
  // }


  const addItemToOrder = (item) => {
    setOrderItems((prevItems) => {
      // Create a unique key for this item based on its ID and add-ons
      const addOnsKey = item.addOns
        ? item.addOns
          .map((addon) => `${addon.name}-${addon.price}`)
          .sort()
          .join("|")
        : ""
      const itemKey = `${item.id}-${item.size || ""}-${addOnsKey}`

      // Find existing configuration
      const existingIndex = prevItems.findIndex((i) => {
        if (i.id !== item.id) return false
        if (i.size !== item.size) return false
        const iKey = i.addOns
          ? i.addOns.map((a) => `${a.name}-${a.price}`).sort().join("|")
          : ""
        return iKey === addOnsKey
      })

      let updatedItems = [...prevItems]
      let updatedItem

      if (existingIndex >= 0) {
        // Increase quantity
        updatedItem = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + 1,
        }
        updatedItem.totalPrice = calculateItemTotalPrice(updatedItem)
        updatedItems[existingIndex] = updatedItem
      } else {
        // New entry
        updatedItem = {
          ...item,
          quantity: item.quantity || 1,
        }
        updatedItem.totalPrice = calculateItemTotalPrice(updatedItem)
        updatedItems.push(updatedItem)
      }

      // Track add-ons mapping
      setItemsWithAddOns((prev) => ({
        ...prev,
        [itemKey]: updatedItem,
      }))

      // === NEW: remember last-added config ===
      setLastAddedItem(updatedItem)
      return updatedItems
    })
  }

  // Function to remove an item from the order
  // const removeItemFromOrder = (itemId, addOns = null, size = null) => {
  //   setOrderItems((prevItems) => {
  //     // Find the item with matching ID and add-ons if specified
  //     const existingItemIndex = prevItems.findIndex((i) => {
  //       if (i.id !== itemId) return false

  //       // If add-ons are specified, check if they match
  //       if (addOns && i.addOns) {
  //         const iAddOnsKey = i.addOns
  //           .map((addon) => `${addon.name}-${addon.price}`)
  //           .sort()
  //           .join("|")
  //         const newAddOnsKey = addOns
  //           .map((addon) => `${addon.name}-${addon.price}`)
  //           .sort()
  //           .join("|")
  //         if (iAddOnsKey !== newAddOnsKey) return false
  //       }

  //       // If size is specified, check if it matches
  //       if (size && i.size !== size) return false

  //       return true
  //     })

  //     if (existingItemIndex >= 0) {
  //       const updatedItems = [...prevItems]
  //       const currentItem = updatedItems[existingItemIndex]

  //       // Create a key for tracking this item configuration
  //       const addOnsKey = currentItem.addOns
  //         ? currentItem.addOns
  //             .map((addon) => `${addon.name}-${addon.price}`)
  //             .sort()
  //             .join("|")
  //         : ""
  //       const itemKey = `${currentItem.id}-${currentItem.size || ""}-${addOnsKey}`

  //       if (currentItem.quantity > 1) {
  //         // If quantity > 1, decrease quantity
  //         const updatedItem = {
  //           ...currentItem,
  //           quantity: currentItem.quantity - 1,
  //         }

  //         // Recalculate the total price
  //         updatedItem.totalPrice = calculateItemTotalPrice(updatedItem)

  //         updatedItems[existingItemIndex] = updatedItem

  //         // Update the items with add-ons tracking
  //         setItemsWithAddOns((prev) => ({
  //           ...prev,
  //           [itemKey]: {
  //             ...updatedItem,
  //             quantity: updatedItem.quantity,
  //             totalPrice: updatedItem.totalPrice,
  //           },
  //         }))
  //       } else {
  //         // If quantity is 1, remove the item
  //         updatedItems.splice(existingItemIndex, 1)

  //         // Update the items with add-ons tracking
  //         setItemsWithAddOns((prev) => {
  //           const updatedTracking = { ...prev }
  //           delete updatedTracking[itemKey]
  //           return updatedTracking
  //         })
  //       }

  //       console.log("Updated items after remove:", updatedItems)
  //       return updatedItems
  //     }

  //     return prevItems
  //   })
  // }


  // Modified remove: if more than one variant of same item exists,
  // open the update‐dialog instead of removing immediately
  // const removeItemFromOrder = (itemId, addOns = null, size = null) => {
  //   // count how many different configs exist for this itemId
  //   const variants = orderItems.filter(i => i.id === itemId)
  //   if (variants.length > 1) {
  //     // open modal so user can pick which one to decrement/remove
  //     // setUpdateDialogItem({ name: variants[0].name, variants })
  //     // setUpdateDialogOpen(true)
  //     openUpdateDialog({ name: variants[0].name, variants }, false);
  //     return
  //   }

  //    // otherwise fall back to normal single‐variant remove:
  //   setOrderItems(prev => {
  //     const idx = prev.findIndex(i => i.id === itemId)
  //     if (idx === -1) return prev
  //     const updated = [...prev]
  //     const cur = updated[idx]
  //     if (cur.quantity > 1) {
  //       const next = { ...cur, quantity: cur.quantity - 1 }
  //       next.totalPrice = calculateItemTotalPrice(next)
  //       updated[idx] = next
  //     } else {
  //       updated.splice(idx, 1)
  //     }
  //     return updated
  //   })
  // }


  const removeItemFromOrder = (itemId, addOns = null, size = null) => {
    // first see how many of this itemId are in the cart at all
    const allVariants = orderItems.filter(i => i.id === itemId)

    // only if you haven't passed in a specific config AND there are >1 variants do we need a modal
    if (!addOns && !size && allVariants.length > 1) {
      openUpdateDialog(
        { name: allVariants[0].name, variants: allVariants },
        false
      )
      return
    }

    // otherwise we either have exactly one variant in the cart, or
    // we've been handed addOns/size so we know which one to adjust.
    setOrderItems(prev => {
      // find the exact index: match id + config (if provided)
      const idx = prev.findIndex(i => {
        if (i.id !== itemId) return false
        if (size && i.size !== size) return false
        if (addOns) {
          // simple name-price fingerprint comparison:
          const keyA = i.addOns
            .map(a => `${a.name}-${a.price}`)
            .sort()
            .join('|')
          const keyB = addOns
            .map(a => `${a.name}-${a.price}`)
            .sort()
            .join('|')
          if (keyA !== keyB) return false
        }
        return true
      })

      if (idx === -1) return prev

      const updated = [...prev]
      const cur = updated[idx]
      if (cur.quantity > 1) {
        const nxt = { ...cur, quantity: cur.quantity - 1 }
        nxt.totalPrice = calculateItemTotalPrice(nxt)
        updated[idx] = nxt
      } else {
        updated.splice(idx, 1)
      }
      return updated
    })
  }

  /**
 * Called by your <UpdateQuantity> dialog’s onSave,
 * passing back an array of variants with new quantities.
 */
  const updateItemSelections = (newSelections) => {
    setOrderItems(prev => {
      // rebuild the orderItems array:
      // 1) keep all items not matching this itemId
      // 2) spread in only those newSelections with quantity > 0
      const otherItems = prev.filter(i => i.id !== newSelections[0].id)
      const updatedVariants = newSelections
        .filter(s => s.quantity > 0)
        .map(s => ({
          ...s,
          totalPrice: calculateItemTotalPrice(s)
        }))
      return [...otherItems, ...updatedVariants]
    })
    setUpdateDialogOpen(false)
    setUpdateDialogItem(null)
  }

  // Get item quantity for dialog display
  const getItemQuantity = (item) => {
    if (!item) return 0

    const addOnsKey = item.addOns
      ? item.addOns
        .map((addon) => `${addon.name}-${addon.price}`)
        .sort()
        .join("|")
      : ""
    const itemKey = `${item.id}-${item.size || ""}-${addOnsKey}`

    return itemsWithAddOns[itemKey]?.quantity || 0
  }

  const getTotalQuantityById = (itemId) =>
    orderItems
      .filter((i) => i.id === itemId)
      .reduce((sum, i) => sum + i.quantity, 0)


  // Reset the order
  const resetOrder = () => {
    setOrderType(null)
    setOrderItems([])
    setCustomerData(null)
    setDialogPrefill(null)
    setItemsWithAddOns({})
    setLastAddedItem(null)
  }

  // The context value
  const value = {
    orderType,
    setOrderType,
    orderItems,
    customerData,
    setCustomerData,
    handleContinueClick,
    addItemToOrder,
    removeItemFromOrder,
    getItemQuantity,
    getTotalQuantityById,
    resetOrder,
    openDialog,
    dialogOpen,
    dialogType,
    closeDialog,
    reopenDialog,
    lastAddedItem,
    dialogPrefill,
    setOrderItems,
    updateDialogOpen,
    updateDialogItem,
    updateItemSelections,
    closeUpdateDialog,
    openUpdateDialog,
    updateDialogFromAdd
  }

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
}

// Custom hook to use the order context
export function useOrder() {
  const context = useContext(OrderContext)
  if (context === undefined) {
    throw new Error("useOrder must be used within an OrderProvider")
  }
  return context
}
